import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronRight, Camera, PartyPopper } from "lucide-react";
import { Progress } from "../components/ui/progress";
import { useFamily } from "../context/FamilyContext";
import { api, errMsg, fmtDate, fmtTime, firstName } from "../lib/api";
import { Chips, PageTitle } from "../components/Bits";

const LANGS = ["English", "Tagalog", "Spanish", "Cantonese", "Vietnamese", "Other"];

function Photo({ path }) {
  const { fid } = useFamily();
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!path) return undefined;
    let u;
    api.get(`/families/${fid}/file`, { params: { path }, responseType: "blob" }).then((r) => {
      u = URL.createObjectURL(r.data);
      setUrl(u);
    }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [path, fid]);
  return url ? <img src={url} alt="Helper" className="h-full w-full object-cover" /> : null;
}

function Profile({ helper, onSaved }) {
  const { fid } = useFamily();
  const [f, setF] = useState({ name: helper.name, availability: helper.availability, languages: helper.languages || [], relationship: helper.relationship });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/families/${fid}/helpers/${helper.id}`, f);
      toast.success("Profile saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/families/${fid}/helpers/${helper.id}/photo`, form);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div data-testid="helper-profile-form" className="hf-card space-y-4">
      <div className="flex items-center gap-4">
        <label className="relative flex h-20 w-20 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-hf-sage text-hf-green" data-testid="helper-photo-upload">
          {helper.photo_path ? <Photo path={helper.photo_path} /> : <Camera className="h-7 w-7" />}
          <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={upload} data-testid="helper-photo-input" />
        </label>
        <p className="text-base text-hf-muted">Add a friendly photo so the family knows who's at the door.</p>
      </div>
      <label className="block text-base font-semibold">Your name</label>
      <input data-testid="helper-name-input" className="hf-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <label className="block text-base font-semibold">When are you usually free?</label>
      <input data-testid="helper-availability-input" className="hf-input" placeholder="Weekday afternoons" value={f.availability} onChange={(e) => setF({ ...f, availability: e.target.value })} />
      <label className="block text-base font-semibold">Languages you speak</label>
      <Chips multi options={LANGS} value={f.languages} onChange={(v) => setF({ ...f, languages: v })} testid="helper-lang" />
      <label className="block text-base font-semibold">How you know the family</label>
      <input data-testid="helper-relationship-input" className="hf-input" value={f.relationship} onChange={(e) => setF({ ...f, relationship: e.target.value })} />
      <button data-testid="helper-profile-save" disabled={busy} onClick={save} className="hf-btn hf-btn-primary w-full">Save profile</button>
    </div>
  );
}

export default function HelperHome() {
  const { helper, base, data, refresh, roles } = useFamily();
  const [showProfile, setShowProfile] = useState(false);
  if (!helper) return <p className="text-lg">No helper yet.</p>;
  const c = helper.checklist || {};
  const items = [
    ["profile", "Your profile", null, () => setShowProfile(!showProfile)],
    ["identity", "Identity check", `${base}/identity`],
    ["background", "Background check", `${base}/background`],
    ["training", "Short training modules", `${base}/training`],
    ["certificates", "Upload certificates (CPR, respite training)", `${base}/certificates`],
    ["agreement", "Sign the helper agreement", `${base}/agreement`],
  ];
  const done = items.filter(([k]) => c[k]).length;
  const ready = helper.status === "ready";
  const next = data.visits.find((v) => v.status === "upcoming" || v.status === "in_progress");
  const cg = firstName(data.family.caregiver_name);
  return (
    <div className="space-y-6">
      <PageTitle eyebrow={`Helping the ${data.family.caregiver_name?.split(" ").slice(-1)[0] || ""} family`} title={`Hi ${firstName(helper.name)}.`} sub={`${cg} trusts you with ${data.family.recipient?.name || "someone they love"}. Thank you for stepping in.`} />
      {ready ? (
        <div data-testid="helper-ready-banner" className="rounded-2xl bg-hf-green p-6 text-hf-cream hf-rise">
          <PartyPopper className="h-8 w-8 text-hf-sun" />
          <p className="mt-3 font-heading text-2xl font-semibold">Welcome to the team.</p>
          <p className="mt-1 text-lg text-hf-cream/85">Status: Ready for first visit</p>
        </div>
      ) : (
        <div className="hf-card">
          <p className="text-base font-semibold text-hf-muted">Getting set up</p>
          <Progress data-testid="helper-checklist-progress" value={(done / items.length) * 100} className="mt-3 h-3 bg-hf-line" />
          <p className="mt-2 text-base text-hf-muted">{done} of {items.length} done</p>
        </div>
      )}
      {next && ready && (
        <Link to={`${base}/visit`} data-testid="helper-next-visit" className="flex items-center justify-between rounded-2xl bg-hf-coralsoft p-5">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-hf-coraldark">{next.status === "in_progress" ? "Visit in progress" : "Next visit"}</p>
            <p className="mt-1 text-xl font-semibold">{fmtDate(next.scheduled_date)} · {fmtTime(next.start_time)}</p>
          </div>
          <ChevronRight className="h-6 w-6 text-hf-coraldark" />
        </Link>
      )}
      <div className="hf-card p-2" data-testid="helper-checklist">
        {items.map(([k, label, to, onClick]) => {
          const inner = (
            <>
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${c[k] ? "bg-hf-green text-hf-cream" : "border-2 border-hf-line"}`}>{c[k] && <Check className="h-5 w-5" />}</span>
              <span className="flex-1 text-lg">{label}</span>
              <ChevronRight className="h-5 w-5 text-hf-muted" />
            </>
          );
          const cls = "flex min-h-[64px] w-full items-center gap-4 rounded-xl px-4 text-left hover:bg-hf-cream";
          return to ? (
            <Link key={k} to={to} data-testid={`checklist-${k}`} className={cls}>{inner}</Link>
          ) : (
            <button key={k} onClick={onClick} data-testid={`checklist-${k}`} className={cls}>{inner}</button>
          );
        })}
      </div>
      {showProfile && roles.includes("helper") && <Profile helper={helper} onSaved={refresh} />}
      <div className="hf-card bg-hf-creamdeep">
        <p className="font-heading text-lg font-semibold">What helpers do not do</p>
        <p className="mt-1 text-lg text-hf-muted">Handoff helpers give non-medical respite only. No medications, injections, wound care, or other medical tasks.</p>
      </div>
    </div>
  );
}
